import { useTranslation } from "react-i18next";
import type { JSX } from "react";
import { memo, useCallback, useMemo, useState } from "react";

import type { INewFastInfiniteProps } from "./types";
import { fastInfiniteVariant } from "./variant";

import { Field } from "@/components/fastFields/field/field";
import { InfiniteSearch } from "@/components/formControls/infiniteSearch";
import type { IMetaOption, IOption } from "@/interfaces";
import { useReadOnly } from "@/hooks";
import type {
  IInfiniteDropdownType,
  IInfiniteListResponse,
} from "@/api/infiniteDropdowns/mapperConfig/config";
import { clearFieldsDependencyValue } from "@/utils";
import { allInfiniteConfig } from "@/api/infiniteDropdowns/mapperConfig/mapper";
import type { ID } from "@/constants";

export const FastInfinite = memo(function FastInfinite<
  TFormik,
  TType extends IInfiniteDropdownType,
>(props: INewFastInfiniteProps<TFormik, TType>) {
  const {
    formik,
    name,
    params,
    type,
    disabled,
    readOnly: PropsReadonly,
    isError: PropsIsError,
    isLoading: propsIsLoading,
    isRequired,
    placeholder,
    dependencies = [],
    multiSelectDependencies,
    title: propsTitle,
    onChange,
    onClear,
  } = props;
  const { values, setFieldValue, errors, touched, handleBlur } = formik;
  const value = values[name] as IOption;

  const readonly = useReadOnly(PropsReadonly);
  const enabled = !readonly;

  const { t } = useTranslation("form");

  const [filter, setFilter] = useState("");

  const extendedParams = {
    filter,
    ...params,
  };

  const config = useMemo(() => allInfiniteConfig[type], [type]);
  const useHook = config.useHook;
  const options = config.hookOptions?.(extendedParams);
  const renderItem = config.itemNode;

  const {
    data,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isLoadingError,
  } = useHook({
    variables: extendedParams,
    enabled,
    staleTime: 60_000,
    ...options,
  });

  const listData = useMemo(
    () => data?.pages.flatMap((page) => config.mapper(page)) || [],
    [config, data],
  );

  const changeHandler = useCallback(
    (val: IOption<ID>) => {
      onChange?.(val as IMetaOption<IInfiniteListResponse[TType]>);
      setFieldValue(name.toString(), val);
      setFilter("");
      clearFieldsDependencyValue<TFormik>(
        setFieldValue,
        dependencies,
        "select",
      );
      clearFieldsDependencyValue<TFormik>(
        setFieldValue,
        multiSelectDependencies,
        "multiSelect",
      );
      onClear?.([name, ...dependencies]);
    },
    [
      onChange,
      setFieldValue,
      name,
      dependencies,
      multiSelectDependencies,
      onClear,
    ],
  );

  const fieldProps = fastInfiniteVariant[type];
  const title = propsTitle ? t(propsTitle) : t(fieldProps.title);

  return (
    <Field
      name={name}
      title={propsTitle || fieldProps.title}
      formik={formik}
      icon={fieldProps.iconName}
      isRequired={isRequired && !readonly}
    >
      <InfiniteSearch
        filter={filter}
        placeholder={placeholder || title}
        id={name.toString()}
        renderItem={
          renderItem
            ? (value) =>
                renderItem?.(value as IMetaOption<IInfiniteListResponse[TType]>)
            : undefined
        }
        onChange={changeHandler}
        isLoading={isFetching || propsIsLoading}
        isError={isLoadingError || PropsIsError}
        onBlur={handleBlur}
        value={value}
        validation={touched[name] && errors[name] ? "error" : undefined}
        readOnly={readonly}
        disabled={disabled}
        data={listData}
        searchInputChangeHandler={(value) => setFilter(value)}
        onGetNext={
          hasNextPage && !isFetchingNextPage ? () => fetchNextPage() : undefined
        }
        isFetchingNextPage={isFetchingNextPage}
      />
    </Field>
  );
}) as unknown as <TFormik, TType extends IInfiniteDropdownType>(
  props: INewFastInfiniteProps<TFormik, TType>,
) => JSX.Element;
